import React from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import { universalTypeFilterId } from '../../../../../common/constants/filters';
import { type UniversalTypeFilterMapping } from '../../../../../common/constants/filters/types';
import { allTypeFilterValueKeys } from '../../../../../common/constants/filters/universal-type';
import {
	type ProductKeys,
	ProductKeys as ProductKeysEnum,
} from '../../../../../common/constants/products';
import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { getTypeMapping } from '../../../../../common/utils/filters/constants';
import { useUniversalSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { jiraWorkTypesForRequest } from '../../../../../controllers/filters/selected/utils';
import { useFiltersDiscoverability2Enabled, usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { useSelectedProduct } from '../../../../../controllers/store/search';

import { i18n } from './messages';

const getOptionsToProducts = (typeMapping: UniversalTypeFilterMapping) => {
	const optionsToProducts: Record<string, ProductKeys[]> = {};
	allTypeFilterValueKeys.forEach((type) => {
		optionsToProducts[type] = typeMapping[type].products || [];
	});
	return optionsToProducts;
};

const TypeFilter = ({
	testId = 'universal-type-filter',
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
}: { testId?: string } & FilterProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const typeMapping = getTypeMapping();
	const optionsToProducts = getOptionsToProducts(typeMapping);
	const [productSelected] = useSelectedProduct();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ setOption, loadInitialOptions, setLookupInput, clearLookupInput, clearOptions },
	] = useUniversalSelectFilter({
		filterId: universalTypeFilterId,
		optionsToProducts,
	});

	if (!filterData) {
		return null;
	}
	// Do not show TypeFilter for Atlas since that is handled by Noun filters
	if (productSelected === ProductKeysEnum.Atlas) {
		return null;
	}

	const { selectedOptions, options, lookupInput } = filterData;

	const filteredOptions = fg('rovo_jira_work_type')
		? options
		: options.filter((option) => !Object.keys(jiraWorkTypesForRequest).includes(option.value));

	const label = fit === 'compact' && selectedOptions.length > 0 ? '' : formatMessage(i18n.type);

	return (
		<SelectFilter
			contentFit={fit}
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={filteredOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(i18n.filterTypePlaceholder)}
			filterOrControlId={universalTypeFilterId}
			searchInput={lookupInput}
			onSearchInputChange={setLookupInput}
			isSearchable
			onClearOptions={clearOptions}
			testId={testId}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default TypeFilter;
